import { useState, useEffect } from "react";
import axios from "axios";
import EditEmployee from './EditEmployee';
import {
  Container,
  Row,
  Col,
  Table,
  Breadcrumb,
  Button,
  Form,
  Card,
  Modal,
  Alert
} from "react-bootstrap";
import * as XLSX from 'xlsx';

const ManageEmployee = () => {
  const [offices, setOffices] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState("");
  const [users, setUsers] = useState([]);
  const [editingEmp, setEditingEmp] = useState(null);

  // 🔹 Modal + Notification state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState("success");

  // Handle edit button click
  const handleEditClick = (user) => {
    setEditingEmp(user);
  };

  // 🔹 Open confirmation modal
  const confirmDelete = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // 🔹 Handle delete after confirmation
  const handleDeleteConfirmed = async () => {
    if (!userToDelete) return;

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found, please log in.");
        return;
      }

      const response = await axios.get(
        `https://housys.skymarkone.in:8001/api/delete-user/${userToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        setUsers((prevUsers) => prevUsers.filter((u) => u.id !== userToDelete.id));
        setAlertVariant("success");
        setAlertMessage(`✅ ${userToDelete.name} deleted successfully.`);
      } else {
        setAlertVariant("danger");
        setAlertMessage(response.data.message || "❌ Failed to delete user.");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setAlertVariant("danger");
      setAlertMessage("❌ An error occurred while deleting the user.");
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);

      // Auto hide alert after 3s
      setTimeout(() => setAlertMessage(null), 3000);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleSave = (updatedEmployee) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === updatedEmployee.id ? updatedEmployee : user
      )
    );
  };

  // Get office name
  const getOfficeName = (officeId) => {
    const office = offices.find((office) => office.office_id === Number(officeId));
    return office ? office.office_name : "Unknown Office";
  };

  // Fetch offices
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get("https://housys.skymarkone.in:8001/api/getoffice", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data?.office) {
          setOffices(response.data.office || []);
        }
      } catch (error) {
        console.error("Error fetching offices:", error.response?.data || error.message);
      }
    };

    fetchOffices();
  }, []);

  // Fetch users by office
  useEffect(() => {
    const fetchUsersByOffice = async () => {
      if (!selectedOffice) return;

      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const response = await axios.get(
          `https://housys.skymarkone.in:8001/api/user/getUserSpecificOffice/${selectedOffice}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (Array.isArray(response.data.data)) {
          setUsers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error.response?.data || error.message);
      }
    };

    fetchUsersByOffice();
  }, [selectedOffice]);

  // Handle Excel download
  const handleDownloadExcel = () => {
    if (users.length === 0) {
      setAlertVariant("warning");
      setAlertMessage("⚠️ No data available to export.");
      setTimeout(() => setAlertMessage(null), 3000);
      return;
    }

    const formatDateTime = (datetime) => {
      if (!datetime) return "N/A";
      const date = new Date(datetime);
      return date.toLocaleString();
    };

    const columns = ["ID", "Name", "Email", "Mobile", "Department", "Created Date"];
    const rows = users.map((user, index) => [
      user.id || index + 1,
      user.name || "N/A",
      user.email || "N/A",
      user.mobile || "N/A",
      user.department || "N/A",
      formatDateTime(user.createdAt),
    ]);

    const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);
    ws['!cols'] = [
      { wch: 8 }, { wch: 20 }, { wch: 25 },
      { wch: 15 }, { wch: 20 }, { wch: 25 }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employee Data");
    const now = new Date();
    const dateTime = now.toISOString().slice(0, 19).replace(/[:]/g, '-').replace('T', '_');
    const fileName = `Employee_Data_${dateTime}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Container fluid className="p-4" style={{ minHeight: "100vh" }}>
      {/* 🔹 Alert Notification */}
      {alertMessage && (
        <Alert variant={alertVariant} className="text-center fw-bold">
          {alertMessage}
        </Alert>
      )}

      <Row className="mb-3">
        <Col xs={12} lg={8}>
          <h2 className="fw-bold">Registered User Details</h2>
          <p className="text-muted">All registered User Details</p>
        </Col>
        <Col xs={12} lg={4} className="d-flex justify-content-lg-end align-items-center">
          <Button
            variant="success"
            onClick={handleDownloadExcel}
            disabled={users.length === 0}
          >
            Export to Excel <i className="bi bi-download ms-1"></i>
          </Button>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Breadcrumb>
            <Breadcrumb.Item href="#">Dashboard</Breadcrumb.Item>
            <Breadcrumb.Item active>Manage Employees</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>

      <Row>
        <Col xs={12} lg={6} className="mb-3">
          <Form.Group controlId="formOfficeSelect">
            <Form.Label className="fw-semibold">
              Select Tenant <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Form.Control
              as="select"
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
            >
              <option value="">Select Tenant</option>
              {offices.map((office) => (
                <option key={office.office_id} value={office.office_id}>
                  {office.office_name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <strong>TOP 100 RECORDS</strong>
            </Card.Header>
            <Card.Body>
              <div style={{ overflowX: "auto" }}>
                <Table bordered hover responsive className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Tenant Name</th>
                      <th>Role Name</th>
                      <th>Name</th>
                      <th>Username</th>
                      <th>Mobile Number</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user, index) => (
                        <tr key={user.id}>
                          <td>{index + 1}</td>
                          <td>{getOfficeName(selectedOffice)}</td>
                          <td>{user.role_id}</td>
                          <td>{user.name}</td>
                          <td>{user.username}</td>
                          <td>{user.mobile_no}</td>
                          <td className="text-center">
                            <Button
                              variant="link"
                              size="sm"
                              className="text-primary p-0 me-2"
                              onClick={() => handleEditClick(user)}
                            >
                              <i className="bi bi-pencil-square fs-5"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="text-danger p-0"
                              onClick={() => confirmDelete(user)}
                            >
                              <i className="bi bi-trash fs-5"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 🔹 Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{userToDelete?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {editingEmp && (
        <EditEmployee
          employee={editingEmp}
          onClose={() => setEditingEmp(null)}
          onSave={handleSave}
        />
      )}
    </Container>
  );
};

export default ManageEmployee;
